/**
 * Air-handling-unit adapter for Tietopohja IA.
 *
 * Generic presentation: `@/lib/kb-ia` + `KbCapabilityCard` / `KbSignalRow` / `KbEquipmentIaBody`.
 * This file owns AHU path topology and live Virta / Riippuvuudet projection only.
 */

import type {
  KBBehaviorDef,
  KBCriterionText,
  KBEquipmentType,
  KBOperationalCriterion,
} from "@/lib/api/agent";
import {
  capabilitySignalsChainStep,
  dossierSignalsForComponent,
  kbDataPointNameFi,
  kbEntityNameFi,
  placementFromBehavior,
  quantityFamilyMatchesStepDataPoint,
  type KbCapabilityLayer,
  type KbComponentInterfacePort,
  type KbDependencyGroup,
  type KbInterfaceFace,
  type KbInterfaceStream,
  type KbPathCalcFamily,
  type KbPathChain,
  type KbPathInterfaceJoin,
  type KbProposedChainRule,
  type KbRuleFacet,
  type KbUnitLevelSignal,
  type KbUnitSignalGroup,
} from "@/lib/kb-ia";

export const AIR_HANDLING_UNIT_TYPE = "air-handling-unit";

/** Draft topology order for auto-joining interface ports (coil order may swap later). */
export const AHU_STREAM_COMPONENT_ORDER: Record<KbInterfaceStream, string[]> = {
  supply: [
    "supply-filter",
    "heat-recovery-unit",
    "heating-coil",
    "cooling-coil",
    "supply-fan",
  ],
  extract: ["extract-filter", "heat-recovery-unit", "extract-fan"],
};

export const AHU_COMPONENT_INTERFACE_PORTS: Record<
  string,
  KbComponentInterfacePort[]
> = {
  "supply-filter": [
    {
      id: "supply_air_temp_before_filter",
      name_fi: "Tuloilma ennen tulosuodatinta",
      face: "before",
      stream: "supply",
    },
    {
      id: "supply_air_temp_after_filter",
      name_fi: "Tuloilma tulosuodattimen jälkeen",
      face: "after",
      stream: "supply",
    },
  ],
  "heat-recovery-unit": [
    {
      id: "supply_air_temp_before_hr",
      name_fi: "Tuloilma ennen LTO:ta (raitis)",
      face: "before",
      stream: "supply",
    },
    {
      id: "supply_air_temp_after_hr",
      name_fi: "Tuloilma LTO:n jälkeen",
      face: "after",
      stream: "supply",
    },
    {
      id: "extract_air_temp_before_hr",
      name_fi: "Poistoilma ennen LTO:ta",
      face: "before",
      stream: "extract",
    },
    {
      id: "extract_air_temp_after_hr",
      name_fi: "Poistoilma LTO:n jälkeen (jäteilma)",
      face: "after",
      stream: "extract",
    },
  ],
  "heating-coil": [
    {
      id: "supply_air_temp_before_heating",
      name_fi: "Tuloilma ennen lämmitystä",
      face: "before",
      stream: "supply",
    },
    {
      id: "supply_air_temp_after_heating",
      name_fi: "Tuloilma lämmityksen jälkeen",
      face: "after",
      stream: "supply",
    },
  ],
  "cooling-coil": [
    {
      id: "supply_air_temp_before_cooling",
      name_fi: "Tuloilma ennen jäähdytystä",
      face: "before",
      stream: "supply",
    },
    {
      id: "supply_air_temp_after_cooling",
      name_fi: "Tuloilma jäähdytyksen jälkeen",
      face: "after",
      stream: "supply",
    },
  ],
  "supply-fan": [
    {
      id: "supply_air_temp_before_fan",
      name_fi: "Tuloilma ennen tuloilmapuhallinta",
      face: "before",
      stream: "supply",
    },
    {
      id: "supply_air_temp_after_fan",
      name_fi: "Tuloilma tuloilmapuhaltimen jälkeen (toimitus)",
      face: "after",
      stream: "supply",
    },
  ],
  "extract-filter": [
    {
      id: "extract_air_temp_before_filter",
      name_fi: "Poistoilma ennen poistosuodatinta",
      face: "before",
      stream: "extract",
    },
    {
      id: "extract_air_temp_after_filter",
      name_fi: "Poistoilma poistosuodattimen jälkeen",
      face: "after",
      stream: "extract",
    },
  ],
  "extract-fan": [
    {
      id: "extract_air_temp_before_fan",
      name_fi: "Poistoilma ennen poistopuhallinta",
      face: "before",
      stream: "extract",
    },
    {
      id: "extract_air_temp_after_fan",
      name_fi: "Poistoilma poistopuhaltimen jälkeen",
      face: "after",
      stream: "extract",
    },
  ],
};

/** Equipment stream ends used when a path face has no neighbor component. */
const AHU_STREAM_EQUIPMENT_ENDS: Record<
  KbInterfaceStream,
  {
    start: { id: string; name_fi: string };
    end: { id: string; name_fi: string };
  }
> = {
  supply: {
    start: { id: "fresh_air_temp", name_fi: "Raitisilma" },
    end: { id: "supply_air_temp", name_fi: "Tuloilma (toimitus / säätö)" },
  },
  extract: {
    start: { id: "extract_air_temp", name_fi: "Poistoilma" },
    end: { id: "exhaust_air_temp", name_fi: "Jäteilma" },
  },
};

/** Component order on a stream (draft topology). */
export function componentOrderOnStream(stream: KbInterfaceStream): string[] {
  return AHU_STREAM_COMPONENT_ORDER[stream];
}

function overlayPortNameFi(
  port: KbComponentInterfacePort,
  kb?: KBEquipmentType,
): KbComponentInterfacePort {
  const name_fi = kb ? kbDataPointNameFi(kb, port.id) : undefined;
  return name_fi ? { ...port, name_fi } : port;
}

function overlayEndNameFi(
  end: { id: string; name_fi: string },
  kb?: KBEquipmentType,
): { id: string; name_fi: string } {
  const name_fi = kb ? kbDataPointNameFi(kb, end.id) : undefined;
  return name_fi ? { ...end, name_fi } : end;
}

function portOn(
  componentId: string,
  stream: KbInterfaceStream,
  face: KbInterfaceFace,
) {
  return (AHU_COMPONENT_INTERFACE_PORTS[componentId] ?? []).find(
    (p) => p.stream === stream && p.face === face,
  );
}

/** Derive the path join for one local interface port from component order. */
export function pathJoinForInterfacePort(
  componentId: string,
  port: KbComponentInterfacePort,
  kb?: KBEquipmentType,
): KbPathInterfaceJoin | null {
  const order = componentOrderOnStream(port.stream);
  const idx = order.indexOf(componentId);
  if (idx < 0) return null;

  if (port.face === "before") {
    if (idx === 0) {
      const end = overlayEndNameFi(
        AHU_STREAM_EQUIPMENT_ENDS[port.stream].start,
        kb,
      );
      return {
        peer_scope: "equipment",
        peer_id: "equipment",
        peer_signal_id: end.id,
        peer_name_fi: end.name_fi,
        source: "order",
      };
    }
    const prevId = order[idx - 1]!;
    const prevAfter = portOn(prevId, port.stream, "after");
    if (!prevAfter) return null;
    const named = overlayPortNameFi(prevAfter, kb);
    return {
      peer_scope: "component",
      peer_id: prevId,
      peer_signal_id: named.id,
      peer_name_fi: named.name_fi,
      source: "order",
    };
  }

  if (idx === order.length - 1) {
    const end = overlayEndNameFi(
      AHU_STREAM_EQUIPMENT_ENDS[port.stream].end,
      kb,
    );
    return {
      peer_scope: "equipment",
      peer_id: "equipment",
      peer_signal_id: end.id,
      peer_name_fi: end.name_fi,
      source: "order",
    };
  }
  const nextId = order[idx + 1]!;
  const nextBefore = portOn(nextId, port.stream, "before");
  if (!nextBefore) return null;
  const named = overlayPortNameFi(nextBefore, kb);
  return {
    peer_scope: "component",
    peer_id: nextId,
    peer_signal_id: named.id,
    peer_name_fi: named.name_fi,
    source: "order",
  };
}

/** Dossier Signaalit for an AHU component — owned points + path interface ports. */
export function dossierSignalsForAhuComponent(
  componentId: string,
  owned: { id: string; name_fi: string; unit?: string; criticality?: string }[],
  kb?: KBEquipmentType,
) {
  const ports = (AHU_COMPONENT_INTERFACE_PORTS[componentId] ?? []).map((port) =>
    overlayPortNameFi(port, kb),
  );
  return dossierSignalsForComponent(componentId, owned, ports, (id, port) =>
    pathJoinForInterfacePort(id, port, kb),
  );
}

function facetForAnalysisId(id: string): KbRuleFacet {
  if (id.includes("pressure")) return "pressure";
  if (id.includes("power")) return "power";
  return "temperature";
}

/** Display chips only — never used for filtering. Prefer live `name_fi`. */
function displayLabelsForComponentIds(
  componentIds: string[],
  kb: KBEquipmentType,
): string[] {
  return componentIds.map((id) => kbEntityNameFi(kb, id));
}

/**
 * Analysis-level path criteria → owning component id(s).
 * Needed until criteria carry home metadata in YAML; language-neutral ids only.
 */
const ANALYSIS_CRITERION_COMPONENTS: Record<string, string[]> = {
  heating_stage_cools_supply_air: ["heating-coil"],
  cooling_stage_heats_supply_air: ["cooling-coil"],
  outdoor_vs_fresh_plausibility: ["heat-recovery-unit"],
  hru_cross_stream_energy_balance: ["heat-recovery-unit"],
  exhaust_warmer_than_extract_in_heating_season: ["heat-recovery-unit"],
};

function componentIdsForPathBehavior(
  behavior: KBBehaviorDef,
  kb: KBEquipmentType,
): string[] {
  const fromSource = (behavior.required_data_points ?? [])
    .map((dp) => dp.source)
    .filter((s): s is string => !!s && kb.components.some((c) => c.id === s));
  if (fromSource.length > 0) return [...new Set(fromSource)];

  const ownedBy = new Map<string, string>();
  for (const c of kb.components) {
    for (const dp of c.data_points) ownedBy.set(dp.id, c.id);
  }
  const fromOwned = (behavior.required_data_point_ids ?? [])
    .map((id) => ownedBy.get(id))
    .filter((id): id is string => !!id);
  return [...new Set(fromOwned)];
}

function ruleFromCriterion(
  criterion: KBOperationalCriterion,
  kb: KBEquipmentType,
  opts: {
    layer: KbCapabilityLayer;
    status: KbProposedChainRule["status"];
    facet?: KbRuleFacet;
    component_ids?: string[];
    compares?: string[];
    criteriaText?: Record<string, KBCriterionText>;
  },
): KbProposedChainRule {
  const text = opts.criteriaText?.[criterion.id];
  const component_ids = opts.component_ids ?? [];
  return {
    id: criterion.id,
    name_fi: criterion.name_fi,
    compares: opts.compares ?? [],
    observation_fi: text?.observation_fi,
    explanation_fi: text?.explanation_fi ?? undefined,
    remedy_fi: text?.remedy_fi ?? undefined,
    layer: opts.layer,
    status: opts.status,
    facet: opts.facet,
    component_ids,
    component_labels_fi: displayLabelsForComponentIds(component_ids, kb),
  };
}

/**
 * Vaikutusketjut: live `analyses[]` + path criteria on chain components +
 * behaviors placed under each chain.
 */
export function buildPathChainsFromKb(
  kb: KBEquipmentType,
  criteriaText: Record<string, KBCriterionText> = {},
): KbPathChain[] {
  const componentById = new Map(kb.components.map((c) => [c.id, c]));

  return (kb.analyses ?? []).map((analysis) => {
    const facet = facetForAnalysisId(analysis.id);
    const status: KbProposedChainRule["status"] =
      analysis.runtime_status === "evaluated" ? "in-kb" : "proposed";
    const byId = new Map<string, KbProposedChainRule>();

    for (const criterion of analysis.operational_criteria ?? []) {
      const component_ids = ANALYSIS_CRITERION_COMPONENTS[criterion.id] ?? [];
      byId.set(
        criterion.id,
        ruleFromCriterion(criterion, kb, {
          layer: "monitoring",
          status,
          facet,
          component_ids,
          criteriaText,
        }),
      );
    }

    // A capability belongs on this Virta only when it actually reads one of the
    // chain's step signals (story #1235) — sitting on the chain via its owning
    // part is not enough on its own: heat-recovery-unit sits on both
    // extract_temperature_chain and extract_pressure_chain, so its temperature
    // criteria must not flood the pressure Virta.
    const signalsThisChain = (
      requiredDataPointIds: string[] | undefined,
    ): boolean =>
      analysis.chain.some((step) =>
        capabilitySignalsChainStep(requiredDataPointIds, step.data_point),
      );

    // Temperature/pressure Virta: also union component-owned criteria of parts on
    // the chain (dual-lens), gated by signalsThisChain. Power Virta stays
    // power-only — otherwise fan ΔP / HRU temperature criteria would drown the
    // tehoketju.
    if (facet !== "power") {
      for (const step of analysis.chain) {
        if (step.component === "equipment") continue;
        const component = componentById.get(step.component);
        if (!component) continue;
        for (const criterion of component.operational_criteria ?? []) {
          if (byId.has(criterion.id)) continue;
          if (!signalsThisChain(criterion.required_data_point_ids)) continue;
          byId.set(
            criterion.id,
            ruleFromCriterion(criterion, kb, {
              layer: "monitoring",
              status: "in-kb",
              facet,
              component_ids: [component.id],
              criteriaText,
            }),
          );
        }
      }
    }

    for (const behavior of kb.behaviors) {
      const placement = placementFromBehavior(behavior);
      if (!placement || placement.kind !== "under-chain") continue;
      if (!placement.underIds.includes(analysis.id)) continue;
      if (byId.has(behavior.id)) continue;
      const primary = behavior.operational_criteria[0];
      const text = primary ? criteriaText[primary.id] : undefined;
      const component_ids = componentIdsForPathBehavior(behavior, kb);
      byId.set(behavior.id, {
        id: behavior.id,
        name_fi: behavior.name_fi,
        compares: behavior.required_data_point_ids ?? [],
        observation_fi: text?.observation_fi,
        explanation_fi: text?.explanation_fi ?? undefined,
        remedy_fi: text?.remedy_fi ?? undefined,
        layer: placement.layer,
        status: "in-kb",
        facet,
        component_ids,
        component_labels_fi: displayLabelsForComponentIds(component_ids, kb),
      });
    }

    // Dual-lens: component-home behaviors also appear as path capabilities, gated
    // by signalsThisChain (story #1235) rather than "part is on this chain" — the
    // same two-facet case as the component-criteria union above. Skip on power
    // Virta — keep tehoketju to power criteria / path-placed behaviors only.
    if (facet !== "power") {
      for (const behavior of kb.behaviors) {
        const placement = placementFromBehavior(behavior);
        if (!placement || placement.kind !== "under-component") continue;
        if (byId.has(behavior.id)) continue;
        if (!signalsThisChain(behavior.required_data_point_ids)) continue;
        const primary = behavior.operational_criteria[0];
        const text = primary ? criteriaText[primary.id] : undefined;
        const component_ids = [...placement.underIds];
        byId.set(behavior.id, {
          id: behavior.id,
          name_fi: behavior.name_fi,
          compares: behavior.required_data_point_ids ?? [],
          observation_fi: text?.observation_fi,
          explanation_fi: text?.explanation_fi ?? undefined,
          remedy_fi: text?.remedy_fi ?? undefined,
          layer: placement.layer,
          status: "in-kb",
          facet,
          component_ids,
          component_labels_fi: displayLabelsForComponentIds(component_ids, kb),
        });
      }
    }

    const calc_families: KbPathCalcFamily[] = [];
    const seenFamilyIds = new Set<string>();
    for (const step of analysis.chain) {
      if (step.component === "equipment") continue;
      const component = componentById.get(step.component);
      for (const family of component?.quantity_families ?? []) {
        if (seenFamilyIds.has(family.id)) continue;
        if (!quantityFamilyMatchesStepDataPoint(family, step.data_point)) {
          continue;
        }
        seenFamilyIds.add(family.id);
        calc_families.push({ component_id: component!.id, family });
      }
    }

    return {
      id: analysis.id,
      name_fi: analysis.name_fi,
      description_fi: analysis.description_fi,
      source: "kb" as const,
      steps: analysis.chain.map((step) => ({
        label_fi: step.label_fi,
        component: step.component,
        data_point: step.data_point,
      })),
      proposed_rules: [...byId.values()],
      calc_families,
    };
  });
}

/**
 * Riippuvuudet: live `dependency_groups[]` + matching `connection_analyses`.
 */
export function buildDependencyGroupsFromKb(
  kb: KBEquipmentType,
  criteriaText: Record<string, KBCriterionText> = {},
): KbDependencyGroup[] {
  const analysesById = new Map(
    (kb.connection_analyses ?? []).map((a) => [a.id, a]),
  );
  const groups = kb.dependency_groups ?? [];

  return groups.map((group) => {
    const rules: KbProposedChainRule[] = [];
    for (const analysisId of group.analysis_ids) {
      const analysis = analysesById.get(analysisId);
      if (!analysis) continue;
      const criteria = analysis.operational_criteria ?? [];
      if (criteria.length > 0) {
        for (const criterion of criteria) {
          rules.push(
            ruleFromCriterion(criterion, kb, {
              layer: "monitoring",
              status: "connection",
              criteriaText,
            }),
          );
        }
      } else {
        // Connection analysis without criteria — still a capability card (kytkentä).
        rules.push({
          id: analysis.id,
          name_fi: analysis.name_fi,
          compares: [],
          observation_fi: analysis.description_fi || undefined,
          layer: "monitoring",
          status: "connection",
        });
      }
    }

    return {
      id: group.id,
      counterpart_type: group.counterpart_type,
      name_fi: group.name_fi,
      description_fi: group.description_fi,
      signals: group.boundary_signals.map((s) => ({
        id: s.id,
        name_fi: s.name_fi,
        side: s.side === "self" ? ("self" as const) : ("peer" as const),
        unit: s.unit || undefined,
      })),
      rules,
    };
  });
}

function unitGroupForSignalId(id: string): KbUnitSignalGroup {
  if (id.includes("outdoor") || id.includes("fresh")) return "outdoor";
  if (id.includes("setpoint")) return "setpoint";
  if (id.includes("flow") || id.includes("airflow")) return "flow";
  if (id.includes("pressure") || id.includes("dp")) return "pressure";
  if (id.includes("extract") || id.includes("exhaust")) return "extract";
  if (id.includes("supply")) return "supply";
  return "supply";
}

/**
 * Laitteen taso Signaalit from `shared_data_points` (+ fan-owned flow/pressure
 * companions that the static catalog listed at unit level).
 */
export function buildUnitLevelSignalsFromKb(
  kb: KBEquipmentType,
): KbUnitLevelSignal[] {
  const shared = kb.shared_data_points ?? [];
  const out: KbUnitLevelSignal[] = shared.map((dp) => ({
    id: dp.id,
    name_fi: dp.name_fi,
    unit: dp.unit,
    criticality: dp.criticality,
    group: unitGroupForSignalId(dp.id),
    owner: "shared" as const,
  }));

  const seen = new Set(out.map((s) => s.id));
  for (const componentId of ["supply-fan", "extract-fan"] as const) {
    const component = kb.components.find((c) => c.id === componentId);
    if (!component) continue;
    for (const dp of component.data_points) {
      if (seen.has(dp.id)) continue;
      if (
        !dp.id.includes("flow") &&
        !dp.id.includes("pressure") &&
        !dp.id.includes("duct")
      ) {
        continue;
      }
      seen.add(dp.id);
      out.push({
        id: dp.id,
        name_fi: dp.name_fi,
        unit: dp.unit,
        criticality: dp.criticality,
        group: unitGroupForSignalId(dp.id),
        owner: componentId,
      });
    }
  }

  return out;
}

/** Component → Virta ids derived from live `analyses[].chain`. */
export function componentAirPathsFromKb(
  kb: KBEquipmentType,
): Record<string, string[]> {
  const map = new Map<string, Set<string>>();
  for (const analysis of kb.analyses ?? []) {
    for (const step of analysis.chain) {
      if (step.component === "equipment") continue;
      const set = map.get(step.component) ?? new Set<string>();
      set.add(analysis.id);
      map.set(step.component, set);
    }
  }
  return Object.fromEntries(
    [...map.entries()].map(([id, set]) => [id, [...set]]),
  );
}

/** Behavior ids whose live home nests under this component. */
export function behaviorIdsUnderComponentFromKb(
  kb: KBEquipmentType,
  componentId: string,
): string[] {
  return kb.behaviors
    .filter(
      (b) =>
        b.home === "component" && (b.under_ids ?? []).includes(componentId),
    )
    .map((b) => b.id);
}
